import React, { useState } from 'react';
import { Tooltip } from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from 'react-router';
import { ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { isJsonObject } from '../../components/util/Helpers';
import { AppThemeConfig } from '../../helpers/sub-nav-contexts/sub-nav-context';
import { useMount } from 'react-use';
import { getAccessibleTextColor } from '../../utilities';
import { InovationChatRequest } from '../../api/types';
import { ChatActions } from '../../api/lambda/ChatActions';
import { useApplication } from '../../components/user/ApplicationProvider';
import { getEnvironmentCode } from '../../helpers/general-helper-functions';
import { useCommonStoreContext } from '../../components/common-store/CommonStoreProvider';

// iconPaths.ts
export const AI_ANALYST_PATH =
	'M1952 2662 c-12 -8 -79 -191 -594 -1632 -330 -924 -303 -839 -284 -885 26 -62 134 -88 179 -42 15 14 71 158 161 412 77 215 143 400 147 413 l9 23 27 -20 c16 -10 52 -25 81 -31 30 -7 56 -18 59 -26 2 -7 -41 -92 -97 -189 -122 -212 -126 -221 -107 -267 36 -85 153 -96 201 -20 58 93 299 517 308 542 15 43 -11 94 -61 120 -23 11 -41 24 -40 28 1 25 77 217 85 215 5 -1 16 -49 24 -105 19 -121 19 -122 72 -149 32 -16 46 -19 64 -11 20 10 299 419 317 465 17 44 -44 102 -101 95 -22 -2 -42 -26 -111 -126 -46 -68 -87 -120 -91 -115 -4 4 -15 60 -24 123 -9 63 -22 120 -29 126 -6 6 -85 55 -174 108 -161 96 -162 97 -152 124 88 228 269 760 265 778 -10 40 -96 69 -134 46z M2012 2147 c-6 -7 -34 -77 -61 -154 l-50 -141 27 -25 c176 -169 441 78 285 264 -48 57 -172 91 -201 56z M1485 1859 c-147 -150 -303 -259 -525 -367 -356 -174 -702 -479 -831 -733 -52 -104 -40 -117 62 -70 174 81 488 84 822 10 59 -14 101 -19 107 -13 10 10 432 1180 434 1203 2 29 -22 19 -69 -30z m-390 -519 c-3 -4 2 -11 11 -14 8 -3 12 -11 8 -17 -4 -7 -3 -9 3 -6 18 12 38 -20 23 -38 -7 -8 -17 -12 -23 -8 -6 3 -7 1 -3 -6 4 -6 0 -14 -8 -17 -9 -3 -14 -10 -11 -14 3 -4 -2 -13 -9 -20 -14 -11 -40 17 -41 45 0 6 -6 11 -12 11 -7 1 -16 3 -20 4 -5 1 -15 3 -23 4 -8 0 -15 8 -15 16 0 8 5 14 12 13 17 -4 64 15 61 24 -6 21 24 54 38 43 7 -7 12 -16 9 -20z m-240 -42 c0 -8 11 -39 25 -67 24 -52 79 -91 135 -95 28 -2 27 -41 0 -42 -11 -1 -36 -9 -55 -20 -19 -10 -38 -17 -42 -16 -4 1 -19 -21 -33 -48 -14 -28 -22 -50 -19 -50 4 0 1 -7 -6 -15 -7 -8 -9 -15 -4 -15 4 0 3 -5 -4 -12 -13 -13 -46 7 -37 23 7 10 -38 100 -51 102 -5 1 -8 5 -7 8 2 6 -49 31 -69 35 -5 1 -10 3 -13 4 -3 1 -10 3 -17 4 -33 3 -14 28 41 52 33 14 59 31 58 38 -1 6 3 10 9 9 9 -2 56 83 60 109 3 18 4 20 17 15 6 -2 12 -11 12 -19z m237 -290 c7 -13 11 -26 10 -30 -1 -5 5 -9 13 -9 8 -1 20 -2 27 -3 17 -2 21 -23 6 -32 -13 -8 -17 -10 -37 -13 -5 0 -14 -15 -20 -32 -6 -18 -16 -29 -23 -27 -12 4 -14 8 -17 44 0 8 -5 13 -9 10 -4 -2 -18 4 -32 14 l-23 18 31 16 c18 9 33 21 33 26 1 6 3 15 4 20 1 6 3 12 4 15 3 13 21 3 33 -17z';
const SUGGESTIONS = ['Top hospitals by revenue', 'Trend of patient care hours', 'Compare Q3 vs Q4'];

const FALLBACK_SUGGESTIONS = [
	'Top hospitals by revenue',
	'Trend of patient care hours',
	'Compare Q3 vs Q4',
];

const SUGGESTIONS_PROMPT =
	'Give me the 3 most common questions a user would ask about their data on this platform. ' +
	'Reply with ONLY the 3 questions, one per line, no numbering, no extra commentary.';

function parseSuggestedQuestions(raw: string): string[] {
	if (!raw || typeof raw !== 'string') return [];

	return raw
		.split('\n')
		.map((line) =>
			line
				.trim()
				// eslint-disable-next-line no-useless-escape
				.replace(/^\d+[\.\)]\s*/, '') // strip "1. " / "1) "
				.replace(/^[-*•]\s*/, '') // strip "- " / "* " / "• "
				.trim()
		)
		.filter((line) => line.length > 0)
		.slice(0, 3);
}

/**
 * Lightens a hex color by a given percentage (0–1) by mixing it toward white.
 * e.g. lightenColor('#ef4444', 0.25) -> a lighter red/orange
 */
function lightenColor(hex: string, amount = 0.25): string {
	const normalized = hex.replace('#', '');
	const full =
		normalized.length === 3
			? normalized
					.split('')
					.map((c) => c + c)
					.join('')
			: normalized;

	const r = parseInt(full.substring(0, 2), 16);
	const g = parseInt(full.substring(2, 4), 16);
	const b = parseInt(full.substring(4, 6), 16);

	const lighten = (channel: number) => Math.round(channel + (255 - channel) * amount);

	const toHex = (channel: number) => channel.toString(16).padStart(2, '0');

	return `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`;
}

/**
 * Converts a hex color to HSL components.
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
	const normalized = hex.replace('#', '');
	const full =
		normalized.length === 3
			? normalized
					.split('')
					.map((c) => c + c)
					.join('')
			: normalized;

	const r = parseInt(full.substring(0, 2), 16) / 255;
	const g = parseInt(full.substring(2, 4), 16) / 255;
	const b = parseInt(full.substring(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Converts HSL components back to a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
	const hue = h / 360;
	const sat = s / 100;
	const light = l / 100;

	const hueToRgb = (p: number, q: number, t: number) => {
		let tt = t;
		if (tt < 0) tt += 1;
		if (tt > 1) tt -= 1;
		if (tt < 1 / 6) return p + (q - p) * 6 * tt;
		if (tt < 1 / 2) return q;
		if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
		return p;
	};

	let r: number;
	let g: number;
	let b: number;

	if (sat === 0) {
		r = g = b = light;
	} else {
		const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
		const p = 2 * light - q;
		r = hueToRgb(p, q, hue + 1 / 3);
		g = hueToRgb(p, q, hue);
		b = hueToRgb(p, q, hue - 1 / 3);
	}

	const toHex = (channel: number) =>
		Math.round(channel * 255)
			.toString(16)
			.padStart(2, '0');

	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Produces a vivid, higher-contrast "bright" version of a color by boosting
 * lightness and saturation in HSL space — unlike lightenColor (which mixes
 * toward white and desaturates), this keeps the hue punchy. e.g. a dark navy
 * (#0a1f4d) becomes a vibrant bright blue rather than a muted grayish-blue.
 */
function brightenColor(hex: string, lightBoost = 20, satBoost = 20): string {
	const { h, s, l } = hexToHsl(hex);
	const newS = Math.min(100, s + satBoost);
	const newL = Math.min(85, l + lightBoost); // cap so it doesn't wash out to near-white
	return hslToHex(h, newS, newL);
}

/**
 * Builds a two-stop linear-gradient CSS string from a single base color,
 * using a vivid brightened shade (not a plain lighten) for the second stop.
 * Used specifically for the AIAnalystPanel header — other gradient usages
 * should keep using buildAccentGradient/lightenColor as-is.
 */
function buildVividAccentGradient(baseColor: string, angle = 135): string {
	const brightShade = brightenColor(baseColor);
	return `linear-gradient(${angle}deg, ${baseColor}, ${brightShade})`;
}

const AIAnalystIcon: React.FC<{
	size?: number;
	color?: string;
	style?: React.CSSProperties;
}> = ({ size = 20, color = '#0894aa', style }) => (
	<svg
		viewBox="0 0 264 277"
		style={{ width: size + 10, height: size + 10, color, ...style }}
		aria-label="AI Analyst"
		role="img"
	>
		<g
			transform="translate(0,277) scale(0.1,-0.1)"
			fill="currentColor"
		>
			<path d={AI_ANALYST_PATH} />
		</g>
	</svg>
);

// Wraps the icon in a small light chip so it always renders in its native
// colors, regardless of what's behind it. This is the safe way to place the
// mark on a dark/colored background — no recoloring, no lost detail.
const AIAnalystBadge: React.FC<{
	size?: number;
	iconSize?: number;
	iconColor?: string;
	chipBackground?: string;
	style?: React.CSSProperties;
}> = ({ size = 32, iconSize, iconColor, chipBackground = '#ffffff', style }) => (
	<div
		style={{
			width: size + 10,
			height: size + 10,
			minWidth: size,
			borderRadius: '50%',
			background: chipBackground,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
			...style,
		}}
	>
		<AIAnalystIcon
			size={iconSize ?? size * 0.6}
			color={iconColor}
		/>
	</div>
);

// "View Chat History" trigger. Navigation is passed in from the parent
// component, since hooks like useNavigate() can only be called inside a
// component (not at module scope).
const ChatHistoryButton: React.FC<{
	color?: string;
	size?: number;
	style?: React.CSSProperties;
	onClick?: () => void;
}> = ({ color = '#8a8f98', size = 14, style, onClick }) => (
	<Tooltip content="View Chat History">
		<button
			type="button"
			aria-label="View Chat History"
			style={{
				border: 'none',
				background: 'transparent',
				padding: 4,
				margin: 0,
				lineHeight: 0,
				cursor: 'pointer',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '20px',
				height: '20px',
				...style,
			}}
			onClick={onClick}
		>
			<FontAwesomeIcon
				icon={faHistory}
				style={{ color, fontSize: size + 15 }}
			/>
		</button>
	</Tooltip>
);

const DEFAULT_HEADER_COLOR = '#0073c3';

const AIAnalystPanel: React.FC = () => {
	const [value, setValue] = useState('');
	const [headerColor, setHeaderColor] = useState(
		() => localStorage.getItem('AppBranding_BackgroundHeaderColor') ?? DEFAULT_HEADER_COLOR
	);
	const { aiSuggestedQuestions, setAiSuggestedQuestions } = useCommonStoreContext();
	const suggestionsLoading = aiSuggestedQuestions === undefined;
	const suggestions = aiSuggestedQuestions ?? FALLBACK_SUGGESTIONS;

	const navigate = useNavigate();
	const { user } = useApplication();

	const handleAsk = (query: string = value) => {
		const trimmed = query.trim();
		if (!trimmed) return;
		navigate(`/discover/analyst?term=${trimmed}`);
	};

	useMount(() => {
		void ZoneSetting.Get(ZoneSettingEnum.applicationWindowTitle).then((response) => {
			if (response.enabled && isJsonObject(response.value)) {
				const decoded = JSON.parse(response.value) as AppThemeConfig;

				if (decoded.header_background_color) {
					localStorage.setItem(
						'AppBranding_BackgroundHeaderColor',
						decoded.header_background_color
					);
					setHeaderColor(decoded.header_background_color);
				}
			}
		});

		// Only prompt the API if the store doesn't already have an answer —
		// this is what makes it "ask once per session" instead of once per
		// mount/navigation.
		if (aiSuggestedQuestions !== undefined) return;

		void (async () => {
			try {
				const userIdentifer = user.account + getEnvironmentCode();
				const request: InovationChatRequest = {
					userId: userIdentifer,
					message: SUGGESTIONS_PROMPT,
					session_id: '',
				} as InovationChatRequest;

				const result = await ChatActions.sendInovationAIMessage(request);

				if (result.status === 'complete' && result.response) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
					const parsed = parseSuggestedQuestions(result.response);
					setAiSuggestedQuestions(parsed.length === 3 ? parsed : FALLBACK_SUGGESTIONS);
				} else {
					// 'pending' or 'error': store the fallback so we don't
					// keep re-prompting every mount for a request that
					// isn't succeeding.
					setAiSuggestedQuestions(FALLBACK_SUGGESTIONS);
				}
			} catch (err) {
				console.error('Failed to fetch suggested questions:', err);
				setAiSuggestedQuestions(FALLBACK_SUGGESTIONS);
			}
		})();
	});

	return (
		<div
			style={{
				width: '100%',
				boxSizing: 'border-box',
				display: 'flex',
				borderRadius: 16,
				overflow: 'hidden',
				border: '1px solid #e4e6ea',
				boxShadow: '0 2px 6px rgba(16,24,40,0.04)',
			}}
		>
			<div
				style={{
					background: buildVividAccentGradient(headerColor),
					padding: '20px 22px',
					minWidth: 220,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: 4,
				}}
			>
				<AIAnalystBadge
					size={34}
					iconSize={20}
					style={{ marginBottom: 6 }}
					iconColor={headerColor}
				/>
				<span
					style={{
						color: getAccessibleTextColor(headerColor ?? '#002E57'),
						fontWeight: 800,
						fontSize: 17,
					}}
				>
					AI Analyst
				</span>
				<span
					style={{
						color: getAccessibleTextColor(headerColor ?? '#002E57'),
						fontSize: 12.5,
					}}
				>
					Powered by your live data
				</span>
				<span
					style={{
						color: getAccessibleTextColor(headerColor ?? '#002E57'),
						fontSize: 11.5,
					}}
				>
					<i>Real responses on real data</i>
				</span>
			</div>

			<div style={{ background: '#fff', padding: 18, flex: 1 }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: 8,
					}}
				>
					<div
						style={{
							fontSize: 11,
							fontWeight: 700,
							letterSpacing: 1,
							color: '#8a8f98',
							textTransform: 'uppercase',
						}}
					>
						Try asking
					</div>
					<ChatHistoryButton
						color="#b0b3b9"
						size={14}
						onClick={() => navigate('/discover/analyst')}
					/>
				</div>
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
					{suggestions.map((s) => (
						<button
							key={s}
							type="button"
							style={{
								border: 'none',
								background: lightenColor(headerColor, 0.85),
								color: headerColor,
								borderRadius: 999,
								padding: '6px 12px',
								fontSize: 12.5,
								cursor: 'pointer',
								fontWeight: 'bold',
							}}
							onClick={() => handleAsk(s)}
						>
							{s}
						</button>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						background: '#f2f3f5',
						borderRadius: 999,
						padding: '6px 6px 6px 14px',
						gap: 10,
					}}
				>
					<input
						value={value}
						onChange={(e) => setValue(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
						placeholder="Ask AI anything..."
						style={{
							flex: 1,
							border: 'none',
							background: 'transparent',
							outline: 'none',
							fontSize: 14,
							color: '#1a1a1a',
							width: '100%',
						}}
					/>
					<button
						type="button"
						style={{
							border: 'none',
							background: headerColor,
							color: getAccessibleTextColor(headerColor ?? '#002E57'),
							borderRadius: 999,
							padding: '7px 16px',
							fontSize: 13,
							fontWeight: 700,
							cursor: 'pointer',
						}}
						onClick={() => handleAsk()}
					>
						Ask
					</button>
				</div>
			</div>
		</div>
	);
};

export default AIAnalystPanel;
